import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

const DEVO_BG = "https://images.unsplash.com/photo-1581713872605-b9dfbc84eaa4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxjYWxtJTIwbW9ybmluZyUyMG1pc3QlMjBuYXR1cmV8ZW58MHx8fHwxNzgyNjMyMTM3fDA&ixlib=rb-4.1.0&q=85";

export default function Devotional() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [devo, setDevo] = useState(null);
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/devotional/${id}`).then((r) => { setDevo(r.data); setStep(0); setDone(false); });
  }, [id]);

  if (!devo) return null;
  const steps = devo.steps;
  const current = steps[step];
  const isLast = step === steps.length - 1;

  const next = async () => {
    if (!isLast) { setStep((s) => s + 1); return; }
    try {
      const { data } = await api.post(`/devotional/${id}/complete`);
      setDone(true);
      toast.success(`Practice complete. Day ${data.streak} of your journey.`);
    } catch { toast.error("Could not save your completion."); }
  };

  if (done) {
    return (
      <div className="relative min-h-screen flex items-center justify-center px-6">
        <img src={DEVO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="relative text-center max-w-lg animate-fade-up">
          <span className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-8 animate-breathe">
            <Check className="w-7 h-7" />
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">Rest in His peace</h1>
          <p className="font-scripture italic text-xl text-primary/80 mt-5 leading-relaxed">"{devo.verse}"</p>
          <p className="text-sm text-muted-foreground mt-2">{devo.verse_ref}</p>
          <div className="flex gap-4 justify-center mt-10">
            <button data-testid="devotional-journal-button" onClick={() => navigate("/journal")}
              className="bg-primary text-primary-foreground rounded-sm px-6 py-3 hover:bg-primary/90 transition-colors">Journal this</button>
            <button data-testid="devotional-home-button" onClick={() => navigate("/")}
              className="border border-border rounded-sm px-6 py-3 hover:bg-muted transition-colors">Return home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <img src={DEVO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
      <div className="relative max-w-2xl mx-auto px-6 py-12 md:py-20">
        <button data-testid="devotional-back-button" onClick={() => navigate("/practices")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-12">
          <ArrowLeft className="w-4 h-4" /> Practices
        </button>

        <p className="label-overline">{devo.title} · {devo.theme}</p>

        {/* progress */}
        <div className="flex gap-1.5 mt-4 mb-16">
          {steps.map((s, i) => (
            <div key={s.label} className={`h-0.5 flex-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-primary" : "bg-border"}`} />
          ))}
        </div>

        <div key={step} className="animate-fade-up min-h-[40vh]">
          <p className="label-overline text-accent mb-6">{current.label} · {step + 1} of {steps.length}</p>
          {current.type === "scripture" ? (
            <blockquote className="font-scripture italic text-3xl sm:text-4xl leading-relaxed text-primary">{current.text}</blockquote>
          ) : (
            <p className="font-heading text-2xl sm:text-3xl leading-snug text-foreground">{current.text}</p>
          )}
        </div>

        <div className="flex items-center justify-between mt-16">
          <button data-testid="devotional-prev-button" disabled={step === 0} onClick={() => setStep((s) => s - 1)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-0">Previous</button>
          <button data-testid="devotional-next-button" onClick={next}
            className="flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-7 py-3.5 hover:bg-primary/90 transition-colors">
            {isLast ? "Complete practice" : "Continue"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
